import { normalizeRecaudo } from "../aseguramiento/model/recaudoModel";
import axiosInstance from "core/api/axiosInstance";

// ==================== FUNCIONES DE UTILIDAD ====================

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return {
      hasToken: false,
      token: null,
      tokenLength: 0,
      hasUser: false,
      userData: null,
    };
  }
};

const handleServiceError = (error, serviceName) => {
  console.error(`❌ Error en ${serviceName}:`, error);

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";

    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage = "Endpoint no encontrado.";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage =
      "La solicitud está tomando más tiempo de lo esperado. El procesamiento podría continuar en segundo plano.";
    errorDetails = { request: error.request, isTimeout: true };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
    isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
  };
};

// ==================== SERVICIO PARA OBTENER DATOS DE RECAUDO ====================

export const getRecaudoDataService = async (params = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get("/api/financial/get-data-invoice", config);
    const { data, status } = response;

    if (!data || status !== 200) {
      throw new Error("No se pudieron obtener los datos de recaudo");
    }

    const responseData = data.data || {};
    if (
      (responseData.code != null && Number(responseData.code) !== 0) ||
      !Array.isArray(responseData.data)
    ) {
      throw new Error(responseData.message || "La respuesta de recaudo no es válida");
    }
    const rawData = responseData.data.filter((item) => item && !item.deletedAt);
    const responseMessage = data.msm || responseData.message || "";
    const processedData = rawData.map(normalizeRecaudo);

    const sortedData = [...processedData].sort((a, b) => b.id - a.id);

    return {
      success: true,
      data: sortedData,
      rawData: rawData,
      total: rawData.length,
      message: responseMessage || "Datos de recaudo obtenidos exitosamente",
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getRecaudoDataService:", error);
    const errorResult = handleServiceError(error, "getRecaudoDataService");
    return {
      success: false,
      data: [],
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA OBTENER ESTADÍSTICAS DE RECAUDO ====================

export const getRecaudoStatsService = async () => {
  try {
    const result = await getRecaudoDataService();

    if (!result.success) {
      return {
        success: false,
        error: result.error,
        stats: {
          total: 0,
          conciliated: 0,
          conciliatedAmount: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          expired: 0,
          totalAmount: 0,
          approvedAmount: 0,
        },
      };
    }

    const data = result.data;

    const stats = {
      total: data.length,
      conciliated: data.filter((item) => item.statusCategory === "conciliated").length,
      conciliatedAmount: data
        .filter((item) => item.statusCategory === "conciliated")
        .reduce((sum, item) => sum + item.amount, 0),
      approved: data.filter((item) => item.statusCategory === "approved").length,
      rejected: data.filter((item) => item.statusCategory === "rejected").length,
      pending: data.filter((item) => item.statusCategory === "pending").length,
      pending_customer: data.filter((item) => item.statusCategory === "pending_customer").length,
      expired: data.filter((item) => item.statusCategory === "expired").length,
      totalAmount: data.reduce((sum, item) => sum + (item.amount || 0), 0),
      approvedAmount: data
        .filter((item) => item.statusCategory === "approved")
        .reduce((sum, item) => sum + (item.amount || 0), 0),
    };

    return {
      success: true,
      stats,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("❌ Error en getRecaudoStatsService:", error);
    return {
      success: false,
      error: error.message,
      stats: {
        total: 0,
        conciliated: 0,
        conciliatedAmount: 0,
        approved: 0,
        rejected: 0,
        pending: 0,
        expired: 0,
        totalAmount: 0,
        approvedAmount: 0,
      },
    };
  }
};

export default {
  getRecaudoDataService,
  getRecaudoStatsService,
};
